import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import Loading from '../../../components/Loading/Loading';
import Modal from '../../../components/Modal/Modal';
import Select from '../../../components/Select/Select';
import { modelCategorizedSelector } from '../../../stores/models';
import { MODEL_DESCRIPTIONS, MODEL_LABEL, ModelType } from './config';

export type ModelOption = {
  value: string;
  label: string;
  category?: string;
  isFirstInCategory?: boolean;
};
interface ModelSelectProps {
  models: ModelType[];
  value: string;
  onChange: (option: ModelType) => void;
  isLoading?: boolean;
}

const ModelSelect = ({
  models,
  value,
  onChange,
  isLoading = false,
}: ModelSelectProps) => {
  const categorizedModels = useRecoilValue(modelCategorizedSelector);
  const { t } = useTranslation();

  // model option for showing description with category separation
  const modelOptions = useMemo(
    () =>
      categorizedModels.map((item) => ({
        value: item.model,
        label:
          MODEL_LABEL[item.model] ||
          item.model
            .split('-')
            .reduce(
              (s, c) => [s, c.charAt(0).toUpperCase() + c.slice(1)].join(' '),
              ''
            ),
        description: MODEL_DESCRIPTIONS[item.model],
        category: item.category,
        isFirstInCategory: item.isFirstInCategory,
      })),
    [categorizedModels]
  );
  return (
    <section className="model-selector">
      {/* A busy overlay has no text of its own, so it carries its name
          directly — an unnamed dialog is what a screen reader would otherwise
          announce. It wires no onClose: there is nothing to dismiss. */}
      <Modal
        isOpen={isLoading}
        hideCloseButton
        aria-label={t('Loading models...') as string}
      >
        <div style={{ padding: '2rem' }}>
          <Loading />
        </div>
      </Modal>
      <Select
        value={value}
        onChange={(option) =>
          option?.value && onChange(option.value as ModelType)
        }
        options={modelOptions}
        optionsPlacement="bottom-center"
        optionsMinWidth="19.8rem"
        width="100%"
        showOptionDescription
      />
    </section>
  );
};

export default ModelSelect;
